import { useQuery } from "@tanstack/react-query";
import { apiRequest, getQueryFn } from "@/lib/queryClient";

/**
 * v3 per-property billing (docs/build/padpulse-build-backend.md B3–B8, B17,
 * B20). One status endpoint drives the app-shell banner, the billing page's
 * "Your plan" card, and whether the legacy per-room plan UI (calculator,
 * module upsells, upgrade modals) renders at all. Legacy accounts get
 * `{ billingModel: null }` and see nothing new.
 */
export const V3_STATUS_QUERY_KEY = ["/api/app/billing/v3/status"] as const;

export type V3State = "trialing" | "active" | "lifetime" | "comped" | "paused" | "past_due" | "research";
export type V3BannerKind = "trial_ending" | "paused" | "past_due" | "lifetime_overage" | "start_subscription";
export type V3BannerAction = "add_card" | "confirm_overage" | "start" | "confirm_saved_card";

export interface V3Banner {
  kind: V3BannerKind;
  message: string;
  action: V3BannerAction;
}

export type V3Refund =
  | { eligible: true; kind: "lifetime" | "annual"; until: string }
  | { eligible: false; code: string; message: string };

export interface V3BillingStatusV3 {
  billingModel: "v3";
  state: V3State;
  readOnly: boolean;
  readOnlyFrom: string | null;
  trialEndsAt: string | null;
  hasCard: boolean;
  banner: V3Banner | null;
  counts: { activeProperties: number; activeOwners: number; billableLocks: number };
  monthlyPreviewCents: number;
  interval: "monthly" | "annual" | null;
  annualAvailable: boolean;
  lifetime: {
    allowance: number;
    source: string | null;
    label: "Founding member" | "Lifetime";
    needsOverageConsent: boolean;
  } | null;
  refund: V3Refund;
  monthlyCancelCopy: string;
  /** A saved card held for confirmation (plan D31/D32, option B). */
  savedCard?: { last4: string | null } | null;
  /** False once the Host trial is used, or after the free research cap (D36). */
  trialAvailable?: boolean;
  subscribeMonthlyCents?: number;
}

export type V3BillingStatus = V3BillingStatusV3 | { billingModel: null | string };

export function isV3Status(data: unknown): data is V3BillingStatusV3 {
  return !!data && typeof data === "object" && (data as { billingModel?: unknown }).billingModel === "v3"
    && typeof (data as { state?: unknown }).state === "string";
}

/** The raw status query. `enabled: false` on public pages (the route needs a session). */
export function useV3BillingStatus({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery<V3BillingStatus | null>({
    queryKey: V3_STATUS_QUERY_KEY,
    queryFn: getQueryFn<V3BillingStatus | null>({ on401: "returnNull" }),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

/** v3 status when this account is on v3 billing, else null (legacy, loading, or error). */
export function useV3Billing(opts?: { enabled?: boolean }): V3BillingStatusV3 | null {
  const query = useV3BillingStatus(opts);
  return isV3Status(query.data) ? query.data : null;
}

/** True only once the status query has confirmed a v3 account. */
export function useIsV3Billing(opts?: { enabled?: boolean }): boolean {
  return useV3Billing(opts) !== null;
}

/**
 * Leaving the app for a Stripe-hosted page. Wrapped in an object so tests can
 * `vi.spyOn(v3Redirect, "to")` instead of driving jsdom navigation.
 */
export const v3Redirect = {
  to(url: string): void {
    window.location.href = url;
  },
};

/** Current /app path (with query) for the card-setup return URL, or undefined off-app. */
export function currentAppReturnPath(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const path = `${window.location.pathname}${window.location.search}`;
  // Must match the server's returnPath schema: /app/… with a narrow charset, ≤ 200 chars.
  return /^\/app\/[A-Za-z0-9/_\-?=&]*$/.test(path) && path.length <= 200 ? path : undefined;
}

/** POST card-setup and send the browser to the Stripe-hosted add-card page. */
export async function startV3CardSetup(returnPath?: string): Promise<void> {
  const res = await apiRequest("POST", "/api/app/billing/v3/card-setup", returnPath ? { returnPath } : {});
  const data = (await res.json()) as { url?: string };
  if (data.url) v3Redirect.to(data.url);
}

export type V3StartResult = { ok: boolean; started?: "trial" | "subscription"; url?: string };

/** POST start: begins the 14-day Host trial (or the subscription on a saved card). Redirects when a card is needed. */
export async function startV3Plan(): Promise<V3StartResult> {
  const res = await apiRequest("POST", "/api/app/billing/v3/start", {});
  const data = (await res.json()) as V3StartResult;
  if (data.url) v3Redirect.to(data.url);
  return data;
}

/** POST confirm-saved-card: "Keep using my card ending ####". */
export async function confirmV3SavedCard(): Promise<{ ok: boolean; resumed?: boolean }> {
  const res = await apiRequest("POST", "/api/app/billing/v3/confirm-saved-card", {});
  return (await res.json()) as { ok: boolean; resumed?: boolean };
}

/** Button copy for the "start" action: a trial when one is left, otherwise the paid plan. */
export function v3StartLabel(status: Pick<V3BillingStatusV3, "trialAvailable" | "subscribeMonthlyCents"> | null): string {
  if (!status || status.trialAvailable !== false) return "Start your 14-day Host trial";
  return `Subscribe — ${formatCentsAsDollars(status.subscribeMonthlyCents ?? 4900)}/month`;
}

/** POST lifetime-addon/confirm: bills the properties above the lifetime allowance. */
export async function confirmV3LifetimeOverage(): Promise<{ ok: boolean; url?: string }> {
  const res = await apiRequest("POST", "/api/app/billing/v3/lifetime-addon/confirm", {});
  const data = (await res.json()) as { ok: boolean; url?: string };
  if (data.url) v3Redirect.to(data.url);
  return data;
}

export function formatCentsAsDollars(cents: number): string {
  const dollars = cents / 100;
  return `$${dollars.toLocaleString("en-US", {
    minimumFractionDigits: Number.isInteger(dollars) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

export const V3_STATE_LABELS: Record<V3State, string> = {
  trialing: "Free trial",
  active: "Active",
  lifetime: "Lifetime",
  comped: "Complimentary",
  paused: "Paused",
  past_due: "Payment due",
  research: "Research (free)",
};
